import {useMemo, useState} from 'react'
import {Typeahead} from 'react-bootstrap-typeahead'
import {parseISO, format, parse} from 'date-fns'
import {useTranslation} from 'react-i18next'
import Chart from 'react-apexcharts'
import 'react-bootstrap-typeahead/css/Typeahead.css'

import {useViewNDVIContext} from '../ViewNDVIContext'
import {formatFixedNumber, formatHectares} from '../../../../../../shared/numberFormat'
import {sortByLabel} from '../../../../../../shared/optionSort'
import {createSelectedOptionAwareFilter} from '../../../../services/typeaheadFilters'
import {buildFieldLandDownloadSubject} from '../../../../Fields/ViewField/ViewLand/Layers/downloadArchive'
import {getApexChartLocaleOptions, getNdviLineChartDisplayOptions} from './apexChartLocale'
import AvailableDatePicker from './AvailableDatePicker'
import RealDateLineChart from './RealDateLineChart'

const landTypeaheadFilterBy = createSelectedOptionAwareFilter('name')

const LandChart = ({landId}) => {
  const {t} = useTranslation()
  const context = useViewNDVIContext()
  const data = useMemo(() => {
    const dates = []
    const avgIndex = []

    let reportDates = context.report.ndvi_land_dates
      .filter((e) => e.land_id == landId)
      .map((e) => {
        const n = {...e}
        n['date'] = parseISO(n['date'])
        return n
      })
      .sort((a, b) => {
        return a.date - b.date
      })

    for (var x in reportDates) {
      const current = reportDates[x]
      const currentDate = format(current.date, 'dd/LL/yyyy', new Date())
      if (current.usage == 'USED') {
        dates.push({
          date: currentDate,
          used: true,
        })
        avgIndex.push({
          x: current.date.getTime(),
          y: current.avg_index,
        })
      } else {
        dates.push({
          date: currentDate,
          used: false,
        })
      }
    }

    const lineChartOptions = getNdviLineChartDisplayOptions({
      dateTimestamps: avgIndex.map((point) => point.x),
    })
    const chart = {
      options: {
        ...lineChartOptions,
        chart: {
          ...getApexChartLocaleOptions(t),
          id: 'avg-index',
        },
        xaxis: {
          ...lineChartOptions.xaxis,
        },
        yaxis: {
          decimalsInFloat: 3,
          labels: {
            formatter: (value) => formatFixedNumber(value, 3),
          },
        },
        tooltip: {
          ...lineChartOptions.tooltip,
          y: {
            formatter: (value) => formatFixedNumber(value, 3),
          },
        },
      },
      series: [],
    }

    chart.series.push({
      name: 'NDVI',
      data: avgIndex,
    })

    return {
      chart,
      dates,
    }
  }, [context.report, landId, t])

  return (
    <>
      <h4 className='mt-5'>{t('reports:ndvi.view.reports.land.avg_ndvi')}</h4>
      <RealDateLineChart
        dateTimestamps={data.chart.series[0]?.data?.map((point) => point.x)}
        options={data.chart.options}
        series={data.chart.series}
      />
    </>
  )
}

const HaDistribution = ({landId}) => {
  const {t} = useTranslation()
  const [openedDatePicker, setOpenedDatePicker] = useState(false)
  const [date, setDate] = useState(null)
  const context = useViewNDVIContext()
  const {availableDates} = useMemo(() => {
    const reports = context.report.ndvi_land_dates.filter(
      (e) => e.land_id == landId && e.usage == 'USED'
    )
    const availableDates = reports.map((e) => format(parseISO(e.date), 'yyyy-LL-dd'))
    if (availableDates.length < 1) {
      return {availableDates: []}
    }

    setDate(parse(availableDates[0], 'yyyy-LL-dd', new Date()))
    return {availableDates}
  }, [landId, context.report])
  const chart = useMemo(() => {
    if (date == null) {
      return null
    }

    const reports = context.report.ndvi_land_dates
      .filter((e) => e.land_id == landId && e.usage == 'USED')
      .filter(
        (e) => format(parseISO(e.date), 'yyyy-LL-dd') == format(date, 'yyyy-LL-dd', new Date())
      )

    if (reports.length < 1) {
      return null
    }

    const report = reports[0]

    const categories = []
    for (var i = 0; i < report.interval.length - 1; i++) {
      categories.push(
        `${formatFixedNumber(report.interval[i].interval, 3)} - ${formatFixedNumber(
          report.interval[i + 1].interval,
          3
        )}`
      )
    }
    categories.push(
      `> ${formatFixedNumber(report.interval[report.interval.length - 1].interval, 3)}`
    )

    return {
      options: {
        chart: {
          ...getApexChartLocaleOptions(t),
          id: 'ha',
        },
        xaxis: {
          categories: categories,
        },
        yaxis: {
          decimalsInFloat: 2,
          labels: {
            formatter: (value) => formatFixedNumber(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => formatHectares(value),
          },
        },
        colors: [
          function ({value, seriesIndex, dataPointIndex, w}) {
            if (dataPointIndex == 0) {
              return '#f1416c'
            } else if (dataPointIndex == 1) {
              return '#ffc700'
            } else if (dataPointIndex == 2) {
              return '#7239ea'
            } else if (dataPointIndex == 3) {
              return '#50cd89'
            }
          },
        ],
      },
      series: [
        {
          name: t('reports:ndvi.view.reports.land.series_hectareas'),
          data: report.interval.map((e) => e.ha),
        },
      ],
    }
  }, [context.report, date, landId, t])

  return (
    <>
      <h4 className='mt-5 mb-5'>{t('reports:ndvi.view.reports.land.ha_distribution')}</h4>
      <AvailableDatePicker
        availableDates={availableDates}
        date={date}
        openedDatePicker={openedDatePicker}
        placeholder={t('reports:ndvi.view.reports.land.select_date')}
        setDate={setDate}
        setOpenedDatePicker={setOpenedDatePicker}
      />
      {chart != null && (
        <>
          <Chart options={chart.options} series={chart.series} type='bar' width='100%' />
          {chart.options.xaxis.categories.map((cat, index) => {
            return (
              <>
                <div className='d-flex fw-semibold align-items-center'>
                  <div
                    className={
                      'bullet w-10px h-10px rounded-2 me-3 ' +
                      ['bg-danger', 'bg-warning', 'bg-info', 'bg-success'][index]
                    }
                  ></div>
                  <div className='flex-grow-1 me-4'>
                    {cat} -{' '}
                    {
                      [
                        t('reports:ndvi.view.status.regular'),
                        t('reports:ndvi.view.status.good'),
                        t('reports:ndvi.view.status.very_good'),
                        t('reports:ndvi.view.status.superior'),
                      ][index]
                    }
                  </div>
                  <div className='fw-bolder text-gray-700 text-xxl-end'>
                    {formatHectares(chart.series[0].data[index])}
                  </div>
                </div>
              </>
            )
          })}
        </>
      )}
    </>
  )
}

const Land = () => {
  const {t} = useTranslation()
  const context = useViewNDVIContext()
  const [landSelection, setLandSelection] = useState([])
  const options = useMemo(() => {
    return sortByLabel(
      context.report.lands.map((land) => {
        return {
          id: land.land_id,
          name: buildFieldLandDownloadSubject({
            fieldName: land.field?.name,
            landName: land.name,
          }),
        }
      }),
      (option) => option.name
    )
  }, [context.report])

  return (
    <>
      <Typeahead
        id='basic-typeahead'
        labelKey='name'
        filterBy={landTypeaheadFilterBy}
        onChange={setLandSelection}
        options={options}
        placeholder={t('reports:ndvi.view.reports.land.selector')}
        selected={landSelection}
      />
      {landSelection.length >= 1 && (
        <>
          <LandChart landId={landSelection[0].id} />
          <HaDistribution landId={landSelection[0].id} />
        </>
      )}
    </>
  )
}

export default Land
